import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import { COMPARE_IDS } from "../../Components/Filters/CompareSelect/presets"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getAlertOverviewDataRequest: ["payload"],
  getAlertOverviewDataSuccess: ["payload"],
  getAlertOverviewDataFailure: ["payload"],

  getAlertsDescriptorDataRequest: ["payload"],
  getAlertsDescriptorDataSuccess: ["payload"],
  getAlertsDescriptorDataFailure: ["payload"],

  getAlertsAccountDataRequest: ["payload"],
  getAlertsAccountDataSuccess: ["payload"],
  getAlertsAccountDataFailure: ["payload"],

  getAlertsCountryDataRequest: ["payload"],
  getAlertsCountryDataSuccess: ["payload"],
  getAlertsCountryDataFailure: ["payload"],

  getAlertsBankDataRequest: ["payload"],
  getAlertsBankDataSuccess: ["payload"],
  getAlertsBankDataFailure: ["payload"],

  getAlertsCardTypeDataRequest: ["payload"],
  getAlertsCardTypeDataSuccess: ["payload"],
  getAlertsCardTypeDataFailure: ["payload"],

  getUsersBySourceDataRequest: ["payload"],
  getUsersBySourceDataSuccess: ["payload"],
  getUsersBySourceDataFailure: ["payload"],

  getAllBusinessAccountsListRequest: null,
  getAllBusinessAccountsListSuccess: ["payload"],
  getAllBusinessAccountsListFailure: ["payload"],

  getDashboardStatisticsRequest: ["payload"],
  getDashboardStatisticsSuccess: ["payload"],
  getDashboardStatisticsFailure: ["payload"],

  getAlertsFlowRequest: ["payload"],
  getAlertsFlowSuccess: ["payload"],
  getAlertsFlowFailure: ["payload"],

  getCreditsSummaryRequest: ["payload"],
  getCreditsSummarySuccess: ["payload"],
  getCreditsSummaryFailure: ["payload"],

  dashboardSetProp: ["key", "value"],

  logout: null
})

export const DashboardTypes = Types
export default Creators

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: null,
  errors: false,

  filtersLastUpd: null,
  filters: {
    period: "27 days",
    compare: { mode: COMPARE_IDS.PREV_PERIOD },
    lastCompare: null,
    alertsType: "count",
    descriptors: "all",
    business_accounts: "all"
  },

  alertsOverviewData: null,
  alertsOverviewFetching: false,

  alertsByDescriptorData: null,
  alertsByDescriptorFetching: false,

  alertsByAccountData: null,
  alertsByAccountFetching: false,

  allBusinessAccountsListFetching: false,
  allBusinessAccountsList: [],

  alertsCountryData: null,
  alertsCountryFetching: false,

  alertsBankData: null,
  alertsBankFetching: false,

  alertsCardTypeData: null,
  alertsCardTypeFetching: false,

  usersBySourceData: null,
  usersBySourceFetching: false,

  statisticBlocksData: [],
  // Shared day axis for the statistics blocks; every block's `series` indexes into it.
  statisticLabels: [],

  // { nodes, links, truncated } - the alert source -> outcome flow behind the Sankey.
  alertsFlowData: null,
  alertsFlowFetching: false,

  // { kpis, triggers, by_source } behind the credits card.
  creditsSummaryData: null,
  creditsSummaryFetching: false
})

/* ------------- Functions for reducer cases ------------- */

const getAlertOverviewDataRequest = (state) => state.merge({ alertsOverviewFetching: true })
const getAlertOverviewDataSuccess = (state, { payload: data }) => {
  const total = data?.result?.data?.reduce((partialSum, el) => partialSum + el, 0)
  const comparisonTotal = data?.comparison?.data?.reduce((s, el) => s + el, 0) ?? null
  return state.merge({
    alertsOverviewFetching: false,
    alertsOverviewData: {
      ...data,
      total,
      comparison: data?.comparison ? { ...data.comparison, total: comparisonTotal } : null
    }
  })
}
const getAlertOverviewDataFailure = (state) => state.merge({ alertsOverviewFetching: false })

const getAlertsDescriptorDataRequest = (state) => state.merge({ alertsByDescriptorFetching: true })
const getAlertsDescriptorDataSuccess = (state, { payload: data }) =>
  state.merge({ alertsByDescriptorFetching: false, alertsByDescriptorData: data })
const getAlertsDescriptorDataFailure = (state) => state.merge({ alertsByDescriptorFetching: false })

const getAlertsAccountDataRequest = (state) => state.merge({ alertsByAccountFetching: true })
const getAlertsAccountDataSuccess = (state, { payload: data }) =>
  state.merge({ alertsByAccountFetching: false, alertsByAccountData: data })
const getAlertsAccountDataFailure = (state) => state.merge({ alertsByAccountFetching: false })

const getAlertsCountryDataRequest = (state) => state.merge({ alertsCountryFetching: true })
const getAlertsCountryDataSuccess = (state, { payload: data }) => {
  const tableData =
    data?.labels?.map((label, idx) => ({
      label,
      count: data.result.count[idx],
      volume: data.result.volume[idx]
    })) || []
  return state.merge({ alertsCountryFetching: false, alertsCountryData: { ...data, tableData } })
}
const getAlertsCountryDataFailure = (state) => state.merge({ alertsCountryFetching: false })

const getAlertsBankDataRequest = (state) => state.merge({ alertsBankFetching: true })
const getAlertsBankDataSuccess = (state, { payload: data }) => {
  let result = data.result.map(
    (el) => ({ ...el, percentage: (el.count / data.total_count) * 100 }) || 0
  )
  return state.merge({ alertsBankFetching: false, alertsBankData: { ...data, result } })
}
const getAlertsBankDataFailure = (state) => state.merge({ alertsBankFetching: false })

const getAlertsCardTypeDataRequest = (state) => state.merge({ alertsCardTypeFetching: true })
const getAlertsCardTypeDataSuccess = (state, { payload: data }) => {
  let result = data.result.map(
    (el) => ({ ...el, percentage: (el.count / data.total_count) * 100 }) || 0
  )
  return state.merge({ alertsCardTypeFetching: false, alertsCardTypeData: { ...data, result } })
}
const getAlertsCardTypeDataFailure = (state) => state.merge({ alertsCardTypeFetching: false })

const getUsersBySourceDataRequest = (state) => state.merge({ usersBySourceFetching: true })
const getUsersBySourceDataSuccess = (state, { payload: data }) =>
  state.merge({ usersBySourceFetching: false, usersBySourceData: data })
const getUsersBySourceDataFailure = (state) => state.merge({ usersBySourceFetching: false })

const getAllBusinessAccountsListRequest = (state) =>
  state.merge({ allBusinessAccountsListFetching: true })
const getAllBusinessAccountsListSuccess = (state, { payload: data }) =>
  state.merge({
    allBusinessAccountsListFetching: false,
    allBusinessAccountsList: data?.business_accounts || data
  })
const getAllBusinessAccountsListFailure = (state) =>
  state.merge({ allBusinessAccountsListFetching: false })

const getDashboardStatisticsRequest = (state) => state.merge({ fetching: true })
const getDashboardStatisticsSuccess = (state, { payload: data }) =>
  state.merge({
    fetching: false,
    statisticBlocksData: data?.data_blocks,
    statisticLabels: data?.labels ?? []
  })
const getDashboardStatisticsFailure = (state) => state.merge({ fetching: false })

// Both breakdowns get their bar percentage the same way every other distribution table
// on this page does: the row's share of its own list's total.
const withRowPercentage = (breakdown) => ({
  ...breakdown,
  result: (breakdown?.result || []).map((el) => ({
    ...el,
    percentage: breakdown?.total_count ? (el.count / breakdown.total_count) * 100 : 0
  }))
})

const getCreditsSummaryRequest = (state) => state.merge({ creditsSummaryFetching: true })
const getCreditsSummarySuccess = (state, { payload: data }) =>
  state.merge({
    creditsSummaryFetching: false,
    creditsSummaryData: {
      ...data,
      triggers: withRowPercentage(data?.triggers),
      by_source: withRowPercentage(data?.by_source)
    }
  })
const getCreditsSummaryFailure = (state) =>
  state.merge({ creditsSummaryFetching: false, creditsSummaryData: null })

const getAlertsFlowRequest = (state) => state.merge({ alertsFlowFetching: true })
const getAlertsFlowSuccess = (state, { payload: flow }) =>
  state.merge({ alertsFlowFetching: false, alertsFlowData: flow })
const getAlertsFlowFailure = (state) =>
  state.merge({ alertsFlowFetching: false, alertsFlowData: null })

export const dashboardSetProp = (state, { key, value }) => {
  const obj = {}
  if (key === "filters") {
    obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state.filters, ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

const logout = (state) => INITIAL_STATE

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_ALERT_OVERVIEW_DATA_REQUEST]: getAlertOverviewDataRequest,
  [Types.GET_ALERT_OVERVIEW_DATA_SUCCESS]: getAlertOverviewDataSuccess,
  [Types.GET_ALERT_OVERVIEW_DATA_FAILURE]: getAlertOverviewDataFailure,

  [Types.GET_ALERTS_DESCRIPTOR_DATA_REQUEST]: getAlertsDescriptorDataRequest,
  [Types.GET_ALERTS_DESCRIPTOR_DATA_SUCCESS]: getAlertsDescriptorDataSuccess,
  [Types.GET_ALERTS_DESCRIPTOR_DATA_FAILURE]: getAlertsDescriptorDataFailure,

  [Types.GET_ALERTS_ACCOUNT_DATA_REQUEST]: getAlertsAccountDataRequest,
  [Types.GET_ALERTS_ACCOUNT_DATA_SUCCESS]: getAlertsAccountDataSuccess,
  [Types.GET_ALERTS_ACCOUNT_DATA_FAILURE]: getAlertsAccountDataFailure,

  [Types.GET_ALERTS_COUNTRY_DATA_REQUEST]: getAlertsCountryDataRequest,
  [Types.GET_ALERTS_COUNTRY_DATA_SUCCESS]: getAlertsCountryDataSuccess,
  [Types.GET_ALERTS_COUNTRY_DATA_FAILURE]: getAlertsCountryDataFailure,

  [Types.GET_ALERTS_BANK_DATA_REQUEST]: getAlertsBankDataRequest,
  [Types.GET_ALERTS_BANK_DATA_SUCCESS]: getAlertsBankDataSuccess,
  [Types.GET_ALERTS_BANK_DATA_FAILURE]: getAlertsBankDataFailure,

  [Types.GET_ALERTS_CARD_TYPE_DATA_REQUEST]: getAlertsCardTypeDataRequest,
  [Types.GET_ALERTS_CARD_TYPE_DATA_SUCCESS]: getAlertsCardTypeDataSuccess,
  [Types.GET_ALERTS_CARD_TYPE_DATA_FAILURE]: getAlertsCardTypeDataFailure,

  [Types.GET_USERS_BY_SOURCE_DATA_REQUEST]: getUsersBySourceDataRequest,
  [Types.GET_USERS_BY_SOURCE_DATA_SUCCESS]: getUsersBySourceDataSuccess,
  [Types.GET_USERS_BY_SOURCE_DATA_FAILURE]: getUsersBySourceDataFailure,

  [Types.GET_ALL_BUSINESS_ACCOUNTS_LIST_REQUEST]: getAllBusinessAccountsListRequest,
  [Types.GET_ALL_BUSINESS_ACCOUNTS_LIST_SUCCESS]: getAllBusinessAccountsListSuccess,
  [Types.GET_ALL_BUSINESS_ACCOUNTS_LIST_FAILURE]: getAllBusinessAccountsListFailure,

  [Types.GET_DASHBOARD_STATISTICS_REQUEST]: getDashboardStatisticsRequest,
  [Types.GET_DASHBOARD_STATISTICS_SUCCESS]: getDashboardStatisticsSuccess,
  [Types.GET_DASHBOARD_STATISTICS_FAILURE]: getDashboardStatisticsFailure,

  [Types.GET_ALERTS_FLOW_REQUEST]: getAlertsFlowRequest,
  [Types.GET_ALERTS_FLOW_SUCCESS]: getAlertsFlowSuccess,
  [Types.GET_ALERTS_FLOW_FAILURE]: getAlertsFlowFailure,

  [Types.GET_CREDITS_SUMMARY_REQUEST]: getCreditsSummaryRequest,
  [Types.GET_CREDITS_SUMMARY_SUCCESS]: getCreditsSummarySuccess,
  [Types.GET_CREDITS_SUMMARY_FAILURE]: getCreditsSummaryFailure,

  [Types.DASHBOARD_SET_PROP]: dashboardSetProp,

  [Types.LOGOUT]: logout
})
